import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { hasInCache } from '../../helper/cacheHelper';
import PartnerSelectionDialog from './PartnerSelectionDialog';

interface ProtectedRouteProps {
  children: React.ReactElement;
  requiresPartner?: boolean;
}

/**
 * Componente que protege rotas que requerem seleção de parceiro
 * Mostra um modal amigável solicitando seleção antes de permitir navegação
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiresPartner = true 
}) => {
  const location = useLocation();
  const [showDialog, setShowDialog] = useState(false);
  const [hasPartner, setHasPartner] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const checkPartner = () => {
      const partnerSelected = hasInCache('CodigoFornecedorSAPSelecionado');
      setHasPartner(partnerSelected);
      setIsInitialized(true);
      
      if (requiresPartner && !partnerSelected) {
        setShowDialog(true);
        setShouldRedirect(false);
      } else {
        setShowDialog(false);
      }
    };

    checkPartner();
    
    // Listener para mudanças no localStorage
    const handleStorageChange = () => {
      checkPartner();
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Custom event para mudanças locais
    window.addEventListener('partnerSelected', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('partnerSelected', handleStorageChange);
    };
  }, [location.pathname, requiresPartner]);

  const handlePartnerSelected = () => {
    // O AutoCompleteParceiro faz reload da página automaticamente
    // quando montaCache=true, então apenas fechamos o modal
    setShowDialog(false);
  };

  const handleClose = () => {
    setShowDialog(false);
    setShouldRedirect(true);
  };

  // Aguardar inicialização
  if (!isInitialized) {
    return null;
  }

  // Se não requer parceiro, renderizar normalmente
  if (!requiresPartner) {
    return children;
  }

  // Se deve redirecionar, fazer redirect
  if (shouldRedirect && !hasPartner) {
    return <Navigate to="/" replace />;
  }

  // Se requer parceiro e não tem, mostrar dialog e bloquear renderização do conteúdo
  if (!hasPartner) {
    return (
      <>
        <PartnerSelectionDialog
          open={showDialog}
          onClose={handleClose}
          onPartnerSelected={handlePartnerSelected}
        />
        {/* Renderizar um placeholder vazio para evitar erros */}
        <div style={{ padding: '20px', textAlign: 'center' }}>
          {/* Aguardando seleção de parceiro */}
        </div>
      </>
    );
  }

  // Se tem parceiro, renderizar conteúdo
  return children;
};

export default ProtectedRoute;
